import React, { useState } from "react";
import { IconButton, Tooltip, keyframes } from "@mui/material";
import { Link } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { getFieldAnchorId } from "../../../../common/fieldAnchorUtils";

export interface FieldAnchorLinkProps {
  fieldName: string;
}

const ANIMATION_DURATION_MS = 300;
const HOVER_SCALE = 1.2;

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(${HOVER_SCALE});
  }
  100% {
    transform: scale(1);
  }
`;

const FieldAnchorLink: React.FC<FieldAnchorLinkProps> = ({ fieldName }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isAnimating, setIsAnimating] = useState(false);

  const handleCopyLink = async () => {
    const anchorId = getFieldAnchorId(fieldName);
    const shareableUrl = `${window.location.origin}${window.location.pathname}#${anchorId}`;

    try {
      await navigator.clipboard.writeText(shareableUrl);
      enqueueSnackbar("Link copied to clipboard!", { variant: "success" });

      // Trigger icon animation
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), ANIMATION_DURATION_MS);
    } catch (error) {
      console.error("Failed to copy link:", error);
      enqueueSnackbar("Failed to copy link", { variant: "error" });
    }
  };

  return (
    <Tooltip title="Copy link to this field">
      <IconButton
        size="small"
        onClick={handleCopyLink}
        sx={{
          marginBottom: 0.5,
          marginLeft: 0.3,
          padding: 0,
          opacity: 0,
          transition: "opacity 0.2s ease, transform 0.2s ease, color 0.2s ease",
          animation: isAnimating
            ? `${pulseAnimation} ${ANIMATION_DURATION_MS}ms ease`
            : "none",
          ".field-label-container:hover &": {
            opacity: 1,
          },
          "&:hover": {
            transform: `scale(${HOVER_SCALE})`,
            color: "primary.main",
          },
        }}
      >
        <Link fontSize="medium" />
      </IconButton>
    </Tooltip>
  );
};

export default FieldAnchorLink;